import Container, { Service } from 'typedi';
import { IFullCashierOptions, IFullCashierPayment, IFullCashierPaymentCallbacks } from './models';
import {
    ActionID,
    IAppLaunchInfo,
    IPerformActionTopicPayload,
    TriggerType,
} from '@sparkware/uc-sdk-core';
import { AppIdentifiers } from '@sparkware/uc-sdk-core';
import { IUnifiedCashierPayment } from './models';
import StringUtils from '../../../Modules/Utils/StringUtils';
import { v4 as uuIdV4 } from 'uuid';
import { Utils } from '../utils';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { WalletService } from '../wallet';
import { SessionSimpleStoreService } from '../storage/implementations/simple-store';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { CookieStoreItemService } from '../storage/implementations';
import { LoaderManager } from '../../loaders/LoaderManager';
import { WindowToken } from '../../injection-tokens';
import { ServiceProduct } from '../gnwp/service-product';
import { INativeService } from '../native/models/INativeService';
import merge from 'lodash/merge';

@Service()
export class PaymentsService {
    private readonly _window: Window;
    private readonly _serviceProduct: ServiceProduct;
    private readonly _utils: Utils;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;
    private readonly _walletService: WalletService;
    private readonly _cookieService: CookieStoreItemService;

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }
    private get _nativeServicePromise(): Promise<INativeService> {
        return LoaderManager.Instance.NativeServiceLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._serviceProduct = Container.get(ServiceProduct);
        this._utils = Container.get(Utils);
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._walletService = Container.get(WalletService);
        this._cookieService = Container.get(CookieStoreItemService);
    }

    async OpenCashier(
        fullCashierData: IFullCashierPayment = {},
        fullCashierCallbacks: IFullCashierPaymentCallbacks = {},
        options: IFullCashierOptions = {},
    ) {
        const { onClose } = fullCashierCallbacks;

        const { browser, isMobile } = this._window.pageContextManager.getDeviceData();

        const isSafari = browser.toLowerCase() === 'safari';
        const isNative = this._utils.findIfIsNative();

        if (!fullCashierData.PromoCode) {
            const sessionPromoCode = this._sessionSimpleStoreService.get(
                StorageItemEnum.CashierPromoCode,
            );
            if (sessionPromoCode) {
                fullCashierData.PromoCode = sessionPromoCode;
            }
        }

        this._sessionSimpleStoreService.remove(StorageItemEnum.CashierPromoCode);

        let clientAutoLoginData = {
            subBrandId: null,
            productPackageId: undefined,
            additionalActivationParameters: undefined,
            width: undefined,
            height: undefined,
            onClose: undefined,
            linkId: undefined,
            target: undefined,
            openInMobileContainer: undefined,
            showCloseButton: undefined,
            paramsToAdd: undefined,
            getUrl: undefined,
            functionToInvoke: undefined,
            classToAdd: undefined,
        };

        const cookieConsent = this._cookieService.get('OptanonConsent');

        if (isMobile || (!isMobile && isSafari && isNative)) {
            clientAutoLoginData.productPackageId = 69;
            if (isNative) {
                clientAutoLoginData.target = 'native';
                clientAutoLoginData.openInMobileContainer = false;
                clientAutoLoginData.showCloseButton = false;
                clientAutoLoginData.paramsToAdd = await this.getNativeParamsForCashier();
            }

            if (!isNative)
                clientAutoLoginData.target = options.openCashierExternallyInMobileEnabled
                    ? '_blank'
                    : '_self';

            //for cashier new tab in safari from native app do we need callbackUrl?
            let callbackUrl: string;
            if (!isNative) {
                const { mainPage } = this._window.pageContextManager.getSiteData();
                callbackUrl =
                    sessionStorage.LastHash != undefined
                        ? location.origin + mainPage + sessionStorage.LastHash
                        : location.origin + mainPage;
            } else {
                callbackUrl = this._window.pageContextManager.getNativeData()?.schemaUrl;
            }

            let specificParams: any = {
                ...fullCashierData,
                SessionType:
                    !fullCashierData.TabId || fullCashierData.TabId === 8
                        ? 16
                        : fullCashierData.TabId,
                PromCode: fullCashierData.PromoCode || '',
                IsAutoLogin: 'False',
                OptanonConsent: cookieConsent && encodeURIComponent(cookieConsent),
                EnableBackButton: 'true',
                LobbyUrl: callbackUrl,
                ...(options?.openCashierThroughRedirect == true && {
                    IsCashierHostedInIframe: 'false',
                }),
            };

            clientAutoLoginData.additionalActivationParameters = this._createSpecificNavigation(
                'XML',
                specificParams,
            );
        } else if (!isMobile) {
            clientAutoLoginData.productPackageId = 11;

            const specificParams = {
                ...fullCashierData,
                SessionType: fullCashierData.TabId || 8,
                PromCode: fullCashierData.PromoCode || '',
                IsCashierHostedInIframe: 'false',
                OptanonConsent: cookieConsent && encodeURIComponent(cookieConsent),
            };

            clientAutoLoginData.additionalActivationParameters = this._createSpecificNavigation(
                'specificNavigation',
                specificParams,
            );

            if (isSafari) {
                clientAutoLoginData.width = null;
                clientAutoLoginData.height = null;
                clientAutoLoginData.target = '_blank';
            }

            if (!isSafari) {
                clientAutoLoginData.width = 1000;
                clientAutoLoginData.height = 665;
                clientAutoLoginData.onClose = !onClose
                    ? () => {
                          const isBalancePollingRequired = this._sessionSimpleStoreService.get(
                              StorageItemEnum.BalancePollingRequired,
                          );
                          if (isBalancePollingRequired === 'true')
                              this._walletService.sendCurrentUserBalanceUpdated();
                      }
                    : onClose;
            }
        }

        if (options?.openCashierThroughRedirect) {
            this.setCashierRedirectOpenProps(clientAutoLoginData);
        }

        const isOpenCashierExternal =
            clientAutoLoginData.target === '_blank' || clientAutoLoginData.target === 'native';
        if (isOpenCashierExternal && typeof onClose === 'function') onClose(null);

        return this._serviceProduct.openServiceAuthenticated({
            subBrandId: clientAutoLoginData.subBrandId,
            productPackageId: clientAutoLoginData.productPackageId,
            additionalActivationParameters: clientAutoLoginData.additionalActivationParameters,
            width: clientAutoLoginData.width,
            height: clientAutoLoginData.height,
            onClose: clientAutoLoginData.onClose,
            linkId: clientAutoLoginData.linkId,
            target: clientAutoLoginData.target,
            openInMobileContainer: clientAutoLoginData.openInMobileContainer,
            showCloseButton: clientAutoLoginData.showCloseButton,
            paramsToAdd: clientAutoLoginData.paramsToAdd,
            getUrl: clientAutoLoginData.getUrl,
            functionToInvoke: clientAutoLoginData.functionToInvoke,
            businessCorrelationID: fullCashierData.BusinessCorrelationID,
        });
    }

    async OpenWidget(unifiedCashierPayment: IUnifiedCashierPayment) {
        const { clientVersion } = this._window.pageContextManager.getDeviceData();
        const cookieConsent = this._cookieService.get('OptanonConsent');

        const defaultLaunchInfo: IAppLaunchInfo = {
            businessCorrelationID: uuIdV4(),
            sequentialCorrelationID: 1,
            channel: unifiedCashierPayment.Channel,
            appSpecificParameters: {
                OptanonConsent: cookieConsent,
                callbackUrl: this.getCallbackURl(),
            },
            trigger: TriggerType.userSelection,
            containerID: undefined,
            sourceAppID: AppIdentifiers.UnifiedClient,
            sourceAppVersion: '0.0.0', // TODO change to correct version
            clientVersion: clientVersion,
        };

        const data: IPerformActionTopicPayload<any, any> = {
            actionID: unifiedCashierPayment?.ActionId || ActionID.deposit,
            correlationID: uuIdV4(),
            launchInfo: merge({}, defaultLaunchInfo, unifiedCashierPayment?.LaunchInfo),
            actionData: {
                AnalyticsData: {
                    action: unifiedCashierPayment.AnalyticsData.action,
                    category: unifiedCashierPayment.AnalyticsData.category,
                    label: unifiedCashierPayment.AnalyticsData.label,
                },
            },
        };

        if (unifiedCashierPayment?.PromoCode)
            Object.assign(data.launchInfo.appSpecificParameters, {
                promoCode: unifiedCashierPayment.PromoCode,
            });

        if (unifiedCashierPayment?.TabId)
            Object.assign(data.launchInfo.appSpecificParameters, {
                tabId: unifiedCashierPayment.TabId,
            });

        if (unifiedCashierPayment?.OnClose)
            Object.assign(data.launchInfo.appSpecificParameters, {
                onClose: unifiedCashierPayment.OnClose,
            });

        const actionHandlerFactory = await this._actionHandlerFactoryPromise;
        await actionHandlerFactory.performAction(data);
    }

    private setCashierRedirectOpenProps(cashierOpenData: any) {
        cashierOpenData.width = '';
        cashierOpenData.height = '';
        cashierOpenData.onClose = '';
        cashierOpenData.target = '_self';
        cashierOpenData.openInMobileContainer = false;
        cashierOpenData.showCloseButton = true;
    }

    private async getNativeParamsForCashier(): Promise<Array<any>> {
        let params: Array<any> = [];
        const nativeService = await this._nativeServicePromise;
        if (nativeService) {
            const { TestData, AppsFlyerInstallData, AppsFlyerID, AppID, IDFA } =
                nativeService.getNativeStoreData();

            if (!StringUtils.isNullOrWhiteSpaces(TestData)) {
                params.push({
                    key: 'TestData',
                    value: '' + TestData + '',
                });
            }

            if (!StringUtils.isNullOrWhiteSpaces(AppsFlyerInstallData)) {
                params.push({
                    key: 'Serial',
                    value: '' + AppsFlyerInstallData + '',
                });
            }

            if (!StringUtils.isNullOrWhiteSpaces(AppsFlyerID)) {
                params.push({
                    key: 'AppsFlyerID',
                    value: '' + AppsFlyerID + '',
                });
            }

            if (!StringUtils.isNullOrWhiteSpaces(AppID)) {
                params.push({
                    key: 'AppID',
                    value: '' + AppID + '',
                });
            }

            if (!StringUtils.isNullOrWhiteSpaces(IDFA)) {
                params.push({
                    key: 'DeviceID',
                    value: '' + IDFA + '',
                });
            }
        }
        return params;
    }

    private _createSpecificNavigation = (tagName, specificParams) => {
        const specificNavigation = Object.keys(specificParams)
            .filter(this._emptyParameterFilter(specificParams))
            .reduce(this._createParamsReducer(specificParams), '');
        return encodeURIComponent(`<${tagName}>${specificNavigation}</${tagName}>`);
    };

    private _emptyParameterFilter = (specificParams) => (key) =>
        typeof specificParams[key] !== 'undefined' &&
        specificParams[key] !== null &&
        specificParams[key] !== '';

    private _createParamsReducer = (specificParams) => (acc, key) =>
        acc + `<${key}>${specificParams[key]}</${key}>`;

    private getDeepLink = () => {
        const deepLink = JSON.stringify({
            area: 'cashier',
            actions: [{ actionID: 'deposit' }],
        });
        return `?deeplink=${deepLink}`;
    };

    private getCallbackURl = () => {
        const clientBaseUrl = this._window.pageContextManager.getSiteData().clientBaseUrl;
        return this._window.location.origin + clientBaseUrl + this.getDeepLink();
    };
}
